import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const WARNING_THRESHOLD = 1 * 60 * 1000; // Show warning 1 minute before timeout

export const useSessionManager = () => {
  const { expireSession, sessionState } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const lastActivityRef = useRef(Date.now());
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset all timers
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only set new timers if session is active
    if (sessionState === 'active') {
      // Set warning timeout
      warningTimeoutRef.current = setTimeout(() => {
        setShowWarning(true);
        setTimeRemaining(WARNING_THRESHOLD / 1000); // Convert to seconds
        
        // Start countdown interval
        intervalRef.current = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev && prev > 0) {
              return prev - 1;
            }
            return 0;
          });
        }, 1000);
      }, INACTIVITY_TIMEOUT - WARNING_THRESHOLD);

      // Set expiry timeout
      expiryTimeoutRef.current = setTimeout(() => {
        setShowWarning(false);
        setTimeRemaining(null);
        expireSession();
      }, INACTIVITY_TIMEOUT);
    }
  }, [sessionState, expireSession]);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    console.log('⏰ updateActivity called, showWarning:', showWarning);
    lastActivityRef.current = Date.now();
    
    // If warning is showing, hide it and reset timers
    if (showWarning) {
      console.log('🚨 Warning was showing, hiding and resetting timers');
      setShowWarning(false);
      setTimeRemaining(null);
      resetTimers();
    } else {
      console.log('⚠️ Warning not showing, should reset timers anyway');
      resetTimers();
    }
  }, [showWarning, resetTimers]);

  // Continue session (user clicked continue in warning modal)
  const continueSession = () => {
    updateActivity();
  };

  // Setup click listener for activity tracking
  useEffect(() => {
    const handleUserActivity = (event: Event) => {
      console.log('🖱️ Click detected:', event.target, 'sessionState:', sessionState);
      if (sessionState === 'active') {
        console.log('📱 Updating activity...');
        updateActivity();
      }
    };

    // Add click listener to document
    console.log('🔧 Adding click listener, sessionState:', sessionState);
    document.addEventListener('click', handleUserActivity);

    return () => {
      console.log('🧹 Removing click listener');
      document.removeEventListener('click', handleUserActivity);
    };
  }, [sessionState, updateActivity]);

  // Setup and cleanup timers
  useEffect(() => {
    // Only manage timers if session is active
    if (sessionState === 'active') {
      resetTimers();
    } else {
      // Clear all timers if session is not active
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setShowWarning(false);
      setTimeRemaining(null);
    }

    return () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionState]);

  return {
    showWarning,
    timeRemaining,
    continueSession,
    updateActivity
  };
};